import { useEffect, useRef, useState } from 'react';
import { PostAuthComponentParams } from '../../types/auth';
import { useIntl } from 'react-intl';
import ContainerLayout from '../ContainerLayout';
import {
  Button,
  Header,
  SpaceBetween,
  TextContent,
  Container,
} from '@amzn/awsui-components-react';
import autoLinkConsentMessages from '../../i18n/autoLinkConsent.messages';
import './autoLinkConsent.css';
import metricsService from '../../services/metrics';
import { IMPRESSIONS, PostAuthRumEvent } from '../../constants/metrics';
import { emitPostAuthRumEvent } from '../../services/emitPostAuthRumEvent';
import { providerMessages } from '../../i18n/provider.messages';
import { IdPs } from '../../constants/providers';
import { MERGE_TYPE_APN_DEPRECATION } from '../../constants/auth';
import signinMessages from '../../i18n/signin.messages';

const AutoLinkConsent = ({ user, onContinue }: PostAuthComponentParams) => {
  const [autoLinkFlowEnabled, setAutoLinkFlowEnabled] =
    useState<boolean>(false);
  const [impressionCaptured, setImpressionCaptured] = useState<boolean>(false);
  const { formatMessage, formatDate } = useIntl();
  const metricsPublisher = useRef(
    metricsService.getPublisher('ProfileMergeNotification')
  );

  const handaleSubmitButton = () => {
    onContinue();
  };

  useEffect(() => {
    if (user.autoLinkConsent?.merge_type === MERGE_TYPE_APN_DEPRECATION) {
      setAutoLinkFlowEnabled(true);
      // Avoid multiple impressions when state changes
      if (!impressionCaptured) {
        emitPostAuthRumEvent(user, PostAuthRumEvent.PROFILE_MERGE_NOTIFICATION);
        metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
        setImpressionCaptured(true);
      }
    } else {
      onContinue();
    }
  }, [user, impressionCaptured, onContinue]);

  if (!autoLinkFlowEnabled) {
    return <></>;
  }

  // Determine which use case applies based on user data
  const isNewUser = user.new_user === true || user.new_user === 'true';

  const getDescription = () => {
    const messageParams = {
      email: user.email,
      date: formatDate(new Date(user.autoLinkConsent!.last_signed_in), {
        dateStyle: 'long',
      }),
      oldProviderName:
        providerMessages[user.autoLinkConsent?.idp as IdPs]?.defaultMessage,
      newProviderName:
        providerMessages[user.providerName as IdPs]?.defaultMessage ||
        formatMessage(signinMessages.organizationSigninButton),
      b: (parts: string[]) => <b>{parts}</b>,
    };

    const messageKey = isNewUser
      ? autoLinkConsentMessages.learningHistoryMergedDescription
      : autoLinkConsentMessages.accountsMergedDescription;

    return formatMessage(messageKey, messageParams);
  };

  return (
    <ContainerLayout>
      <Container>
        <SpaceBetween size={'l'} direction="vertical">
          <Header
            data-testid="autolink-merge-header"
            variant="h1"
            className="large-header"
          >
            {formatMessage(autoLinkConsentMessages.pageTitle)}
          </Header>
          <TextContent>{getDescription()}</TextContent>
          <div className="button-right">
            <Button
              variant="primary"
              onClick={handaleSubmitButton}
              data-testid="continue-button"
            >
              {formatMessage(autoLinkConsentMessages.button)}
            </Button>
          </div>
        </SpaceBetween>
      </Container>
    </ContainerLayout>
  );
};

export default AutoLinkConsent;
